/**
 * 从图片中提取盲水印token
 * @param {string|Blob} imageSource 图片URL或Blob对象
 * @returns {Promise<string>} 提取的token字符串
 * @throws 当图片无效或水印数据不完整时抛出错误
 */
export async function extractToken(imageSource) {
    return new Promise((resolve, reject) => {
        try {
            const img = new Image();
            
            img.onload = function() {
                try {
                    // 创建canvas来读取像素数据
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    
                    canvas.width = img.width;
                    canvas.height = img.height;
                    
                    const width = img.width;
                    const height = img.height;
                    const totalPixels = width * height;
                    
                    // 绘制图片到canvas
                    ctx.drawImage(img, 0, 0);
                    
                    // 获取图片像素数据
                    const imageData = ctx.getImageData(0, 0, width, height);
                    const data = imageData.data; // RGBA格式，每像素4字节
                    
                    // 提取前32位长度信息
                    let dataLength = 0;
                    let bitIndex = 1; // 从第1位开始（跳过第0位）
                    
                    for (let i = 0; i < 32; i++) {
                        // 计算原始RGB格式的索引
                        const rgbIndex = bitIndex * 3 - 1;
                        const pixelIdx = Math.floor(rgbIndex / 3);
                        
                        if (pixelIdx >= totalPixels) {
                            throw new Error('图片不包含有效水印数据');
                        }
                        
                        // 转换为RGBA格式的索引：RGB索引 -> RGBA索引
                        const rgbaIndex = pixelIdx * 4 + (rgbIndex % 3);
                        const channelValue = data[rgbaIndex];
                        
                        // 获取通道最低位 (LSB)
                        const bit = channelValue & 0x01;
                        dataLength = (dataLength << 1) | bit;
                        bitIndex++;
                    }
                    
                    // 验证数据长度
                    if (dataLength <= 0 || dataLength > (totalPixels - 32) / 8) {
                        throw new Error('无效的水印数据长度');
                    }
                    
                    // 提取token字节数据
                    const tokenBytes = [];
                    for (let i = 0; i < dataLength; i++) {
                        let byte = 0;
                        
                        for (let j = 0; j < 8; j++) {
                            // 使用相同的索引转换逻辑
                            const rgbIndex = bitIndex * 3 - 1;
                            const pixelIdx = Math.floor(rgbIndex / 3);
                            
                            if (pixelIdx >= totalPixels) {
                                throw new Error('水印数据不完整');
                            }
                            
                            const rgbaIndex = pixelIdx * 4 + (rgbIndex % 3);
                            const channelValue = data[rgbaIndex];
                            const bit = channelValue & 0x01;
                            
                            byte = (byte << 1) | bit;
                            bitIndex++;
                        }
                        tokenBytes.push(byte);
                    }
                    
                    // 转换为字符串
                    const token = new TextDecoder('utf-8').decode(new Uint8Array(tokenBytes));
                    resolve(token);
                    
                } catch (error) {
                    reject(new Error(`提取水印失败: ${error.message}`));
                }
            };
            
            img.onerror = function() {
                reject(new Error('图片加载失败'));
            };
            
            // 设置图片源
            if (imageSource instanceof Blob) {
                img.src = URL.createObjectURL(imageSource);
            } else {
                img.src = imageSource;
            }
            
            // 处理跨域
            img.crossOrigin = 'anonymous';
            
        } catch (error) {
            reject(new Error(`初始化失败: ${error.message}`));
        }
    });
}

/**
 * 验证token是否有效
 * @param {string} token 提取的token
 * @returns {boolean} 是否为有效的token格式
 */
export function validateToken(token) {
    // 检查是否为32位16进制字符串
    return /^[a-f0-9]{32}$/i.test(token);
} 